import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

export type ShortcutItem = {
  keys: string[];
  label: string;
  description: string;
  enabled: boolean;
  disabledReason?: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  shortcuts: ShortcutItem[];
};

/**
 * Task #29 — keyboard shortcuts overlay. Originally shipped with hard-coded
 * packing shortcuts; Task #31 promoted it out of the packing folder so every
 * page can register its own shortcuts via `useRegisterShortcuts`. Shortcuts
 * that are currently disabled (permissions, missing customer email, status
 * doesn't allow the transition) are rendered greyed-out with the reason so a
 * new operator can see why a key isn't doing anything.
 */
export function ShortcutsHelpModal({ open, onOpenChange, title, shortcuts }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dialog-shortcuts-help">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Press <kbd className="px-1 py-0.5 border rounded text-[10px]">?</kbd> any time to open this help.
          </DialogDescription>
        </DialogHeader>
        {shortcuts.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="text-shortcuts-empty">
            No page-specific shortcuts here. Press{" "}
            <kbd className="px-1 py-0.5 border rounded text-[10px]">?</kbd> on any page that
            advertises shortcuts to see them.
          </p>
        ) : (
          <ul className="space-y-2.5">
            {shortcuts.map((s) => (
              <li
                key={s.label}
                className={`flex items-start gap-3 ${s.enabled ? "" : "opacity-50"}`}
                data-testid={`shortcut-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-center gap-1 shrink-0 w-20">
                  {s.keys.map((k, i) => (
                    <kbd
                      key={i}
                      className="px-1.5 py-0.5 border rounded text-[10px] font-mono bg-muted"
                    >
                      {k}
                    </kbd>
                  ))}
                </div>
                <div className="flex-1 text-sm">
                  <div className="font-medium">{s.label}</div>
                  <div className="text-xs text-muted-foreground">{s.description}</div>
                  {!s.enabled && s.disabledReason ? (
                    <Badge variant="outline" className="mt-1 text-[10px] font-normal">
                      Disabled: {s.disabledReason}
                    </Badge>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
